import { AuthApi } from '../auth-api';
import { TokenManager } from '../token-manager';
import { LoginRequest, LoginResponse, TokenInfo, DecodedJWT } from '../types';
import { jwtDecode } from 'jwt-decode';

/**
 * AuthRepository - Data Access Layer
 * Abstracts authentication data sources (cortex_session vs internal tokens)
 */
export class AuthRepository {
  constructor(
    private readonly authApi: AuthApi,
    private readonly tokenManager: TokenManager
  ) { }

  /**
   * Attempt to get authentication from cortex_session token
   * Token already validated by getCortexSession()
   * Requests refresh if token is near expiration
   */
  async tryGetCortexSessionAuth(): Promise<TokenInfo | null> {
    try {
      const cortexSession = await this.tokenManager.getCortexSession();
      
      if (!cortexSession) {
        return null;
      }

      // Validate token and request refresh if near expiration (< 5 minutes)
      const isValid = await this.tokenManager.isCortexSessionValid(true);
      if (!isValid) {
        console.warn('cortex_session token near expiration or expired');
        return null;
      }

      const decoded = jwtDecode<DecodedJWT>(cortexSession);
      const operatorId = (decoded.OperatorId || decoded.operatorId || decoded.actor)?.toString();
      const distributorId = (decoded.DistributorId || decoded.distributorId)?.toString();
      const accountId = (decoded.AccountId || decoded.accountId)?.toString();

      return {
        appToken: cortexSession,
        accountToken: cortexSession,
        accountId: accountId || 'unknown',
        operatorId: operatorId || 'unknown',
        distributorId: distributorId,
        expiry: new Date(decoded.exp * 1000)
      };
    } catch (error) {
      console.warn('Failed to get cortex_session auth:', error);
      return null;
    }
  }

  /**
   * Login with credentials
   */
  async loginWithCredentials(request: LoginRequest): Promise<LoginResponse> {
    return this.authApi.login(request);
  }

  /**
   * Save tokens to storage
   */
  async saveTokens(
    appToken: string,
    accountToken: string,
    accountId: string,
    operatorId?: string,
    distributorId?: string
  ): Promise<void> {
    await this.tokenManager.saveTokens(appToken, accountToken, accountId, operatorId, distributorId);
  }

  /**
   * Clear all stored tokens
   */
  async clearTokens(): Promise<void> {
    await this.tokenManager.clearTokens();
  }

  /**
   * Check if stored tokens are valid
   */
  async isTokenValid(): Promise<boolean> {
    return this.tokenManager.isTokenValid();
  }
}
